import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9205Api } from '../../services/pw9205Api';
import { usePW9205Mode } from './hooks/usePW9205Mode';
import { usePW9205Record } from './hooks/usePW9205Record';
import { usePW9205Navigation } from './hooks/usePW9205Navigation';
import { usePW9205Lookup } from './hooks/usePW9205Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import ActionBar from './components/ActionBar';
import FormFields from './components/FormFields';
import './PW9205Form.css';

const PW9205Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9205);

  // Hooks
  const { mode, setMode, isEditing } = usePW9205Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames, loadEmployeeProxies } = usePW9205Lookup();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9205Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (docNo: string) => {
    const result = await loadRecord(docNo);
    if (result) {
      await resolveAllNames(result);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW9205Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ──

  const handleAdd = useCallback(() => {
    if (isEditing) {
      // Save
      handleSave(mode, (result) => {
        applyResult(result);
        setMode('browse');
        loadList().then(ids => {
          const idx = ids.indexOf(result.f9051_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      // Start add mode
      clearNames();
      resetForAdd();
      setMode('add');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, clearNames, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      // Start edit mode
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除異動單「${record.f9051_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          } else {
            setCurrentIdx(-1);
            clearNames();
            clearFieldErrors();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, clearFieldErrors]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9205Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查無資料');
        return;
      }
      const ids = (res.data || []).map(r => r.f9051_01);
      setCurrentIdx(0);
      await loadRecordWithNames(ids[0]);
    } catch {
      message.error('查詢失敗');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  // When employee code changes in add mode, load proxies from F905
  const handleEmployeeBlur = useCallback(async (empCode: string) => {
    // Always resolve employee name
    await resolveSingleName('employee', 'employee', empCode);
    // In add mode, also load current proxies to populate original proxy fields
    if (mode === 'add' && empCode.trim()) {
      const proxy = await loadEmployeeProxies(empCode);
      // Apply original proxy fields from F905
      const proxyFieldMap: Array<[keyof typeof formData, string]> = [
        ['f9051_11', (proxy.f9051_11 as string) || ''],
        ['f9051_12', (proxy.f9051_12 as string) || ''],
        ['f9051_13', (proxy.f9051_13 as string) || ''],
        ['f9051_14', (proxy.f9051_14 as string) || ''],
        ['f9051_15', (proxy.f9051_15 as string) || ''],
      ];
      for (const [key, val] of proxyFieldMap) {
        setField(key, val);
      }
    }
  }, [mode, resolveSingleName, loadEmployeeProxies, setField, formData]);

  // ── Search state ──
  const [searchText, setSearchText] = React.useState('');

  return (
    <PermissionGuard module={Modules.PW9205} action="query">
      <Spin spinning={loading}>
        <div className="pw9205-form">
          <div className="page-wrapper">

            {/* Title */}
            <div className="page-title-bar">
              <h1>職務代理人異動單 (pw9205f1)</h1>
              <span className="nav-info">
                {allIds.length > 0
                  ? `${currentIdx + 1} / ${allIds.length}`
                  : '無資料'}
              </span>
            </div>

            {/* Action Bar */}
            <ActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              allIdsLength={allIds.length}
              permissions={perms}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onNavTo={handleNavTo}
              onSearch={() => handleSearch(searchText)}
              onExit={() => navigate('/erp')}
            />

            {/* Search Bar */}
            <div className="search-bar">
              <Input
                placeholder="輸入異動單號或員工編號查詢"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                disabled={isEditing}
                style={{ width: 260 }}
                maxLength={20}
                suffix={<SearchOutlined />}
              />
              <button
                className="btn"
                disabled={isEditing}
                onClick={() => handleSearch(searchText)}
              >
                查詢
              </button>
              <button
                className="btn"
                disabled={isEditing}
                onClick={async () => {
                  setSearchText('');
                  const ids = await loadList();
                  if (ids.length > 0) {
                    setCurrentIdx(0);
                    await loadRecordWithNames(ids[0]);
                  }
                }}
              >
                全部
              </button>
            </div>

            {/* Form Content */}
            <div className="form-content">
              <div className="section-divider">職務代理人異動資料</div>
              <FormFields
                names={names}
                isEditing={isEditing}
                mode={mode}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                onEmployeeBlur={handleEmployeeBlur}
                getFieldError={getFieldError}
              />
            </div>

          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9205Form;
